/* eslint-disable max-lines */
import { IGeoCodedSearchDetails } from '../../types/geo';
import {
  EHeatingSourceType,
  EHouseMaterialType,
  ELocationType,
  ENewobjectType,
  EOtherCommunications,
  EReadyStatus,
  ESaleStatus,
  EVillageClass,
  IGetVillageListRequestSchema,
} from '../../../node/repositories/countryside-search/entities/schemas/GetVillageListRequestSchema';
import { DEFAULT_EXTRA_FILTERS } from '../../constants/filters/defaultExtraFilters';
import { getDefaultVillageListRequest } from '../../interfaces/villages';
import { isArrayWithItems } from '../../utils';
import { EGeoActions } from '../geo/actions';
import { Action, EFilterActions } from './actions';
import { addOptionalFilter, toggleValueInArray } from './utils';

export const filtersReducer = (state: IGetVillageListRequestSchema, action: Action): IGetVillageListRequestSchema => {
  switch (action.type) {
    case EFilterActions.ChangePage: {
      return { ...state, p: action.payload };
    }
    case EFilterActions.ChangeNewObjectTypes: {
      return {
        ...state,
        newobjectType: isArrayWithItems(action.payload)
          ? (action.payload as ENewobjectType[])
          : getDefaultVillageListRequest().newobjectType,
        p: 1,
      };
    }
    case EFilterActions.ChangeVillageClasses: {
      return {
        ...state,
        villageClass: isArrayWithItems(action.payload)
          ? (action.payload as EVillageClass[])
          : getDefaultVillageListRequest().villageClass,
        p: 1,
      };
    }
    case EFilterActions.ChangeReadyStatuses: {
      return {
        ...state,
        readyStatus: addOptionalFilter(action.payload as EReadyStatus[]),
      };
    }
    case EFilterActions.ChangeHouseMaterials: {
      return {
        ...state,
        houseMaterialType: addOptionalFilter(action.payload as EHouseMaterialType[]),
      };
    }
    case EFilterActions.ChangeSaleStatuses: {
      return {
        ...state,
        saleStatus: addOptionalFilter(action.payload as ESaleStatus[]),
      };
    }
    case EFilterActions.ChangeHeatingSourceTypes: {
      return {
        ...state,
        heatingSourceType: addOptionalFilter(action.payload as EHeatingSourceType[]),
      };
    }
    case EFilterActions.ChangeOtherCommunications: {
      return {
        ...state,
        otherCommunications: addOptionalFilter(action.payload as EOtherCommunications[]),
      };
    }
    case EFilterActions.ChangeNewObjectType: {
      return {
        ...state,
        newobjectType: action.payload.value as ENewobjectType[],
        p: 1,
      };
    }
    case EFilterActions.ChangeVillageClass: {
      return {
        ...state,
        villageClass: action.payload.value as EVillageClass[],
        p: 1,
      };
    }
    case EFilterActions.ChangeFinishDate: {
      const newFinishDate = action.payload;

      return {
        ...state,
        finishDate: state.finishDate === newFinishDate || !newFinishDate ? undefined : newFinishDate,
        p: 1,
      };
    }
    case EFilterActions.ChangePriceRange: {
      return {
        ...state,
        p: 1,
        priceFrom: action.payload.min,
        priceTo: action.payload.max,
      };
    }
    case EFilterActions.ChangeCommuteDistance: {
      return {
        ...state,
        commuteKmFrom: action.payload.min,
        commuteKmTo: action.payload.max,
        p: 1,
      };
    }
    case EFilterActions.ChangeCommuteType: {
      return {
        ...state,
        commuteDistanceType: action.payload,
        p: 1,
      };
    }
    case EGeoActions.GeocodedForSearchFetched: {
      const lastDetail = Array.isArray(action.payload.details)
        ? (action.payload.details.filter(detail => !!detail.id).pop() as IGeoCodedSearchDetails)
        : null;
      if (!lastDetail) {
        return state;
      }

      return {
        ...state,
        locationId: lastDetail.id,
        locationType: lastDetail.geoType?.toLowerCase() as ELocationType,
        p: 1,
        regionId: action.payload.regionId,
      };
    }
    case 'FilterRegionChanged': {
      const { id, regionId, parentId } = action.region;

      return {
        ...state,
        locationId: id as number,
        locationType: ELocationType.Location,
        p: 1,
        regionId: parentId || regionId || id,
      };
    }
    case 'GetRegionByDomainFetched': {
      return {
        ...state,
        regionId: action.payload.id,
      };
    }
    case EFilterActions.ChangeReadyStatus: {
      return {
        ...state,
        readyStatus: toggleValueInArray(action.payload, state.readyStatus),
      };
    }
    case EFilterActions.ChangeSaleStatus: {
      return {
        ...state,
        saleStatus: toggleValueInArray(action.payload, state.saleStatus),
      };
    }
    case EFilterActions.ChangeCommunication: {
      return {
        ...state,
        otherCommunications: toggleValueInArray(action.payload, state.otherCommunications),
      };
    }
    case EFilterActions.ChangeHouseMaterial: {
      return {
        ...state,
        houseMaterialType: toggleValueInArray(action.payload, state.houseMaterialType),
      };
    }
    case EFilterActions.ChangeHeatingSourceType: {
      const isWithoutInArray =
        Array.isArray(state.heatingSourceType) && state.heatingSourceType.includes(EHeatingSourceType.Without);

      return {
        ...state,
        heatingSourceType:
          action.payload !== 'without'
            ? toggleValueInArray(action.payload, state.heatingSourceType).filter(
                value => value !== EHeatingSourceType.Without,
              )
            : isWithoutInArray
              ? []
              : [action.payload],
      };
    }
    case EFilterActions.ChangeWcLocation: {
      return {
        ...state,
        wcLocationType: state.wcLocationType !== action.payload ? action.payload : undefined,
      };
    }
    case EFilterActions.ChangeFloorCountFrom: {
      return {
        ...state,
        floorCountFrom: action.payload,
      };
    }
    case EFilterActions.ChangeFloorCountTo: {
      return {
        ...state,
        floorCountTo: action.payload,
      };
    }
    case EFilterActions.ChangeAreaFrom: {
      return {
        ...state,
        areaFrom: action.payload,
      };
    }
    case EFilterActions.ChangeAreaTo: {
      return {
        ...state,
        areaTo: action.payload,
      };
    }
    case 'BuilderIdRemoved': {
      return {
        ...state,
        builderId: undefined,
      };
    }
    case EFilterActions.AddBuilderId: {
      return {
        ...state,
        builderId: action.payload,
      };
    }
    case 'IApplyLastFilters': {
      return action.filters;
    }
    case 'RoadFilterAdded': {
      return {
        ...state,
        road: action.payload,
      };
    }
    case EFilterActions.ChangeRoads: {
      return {
        ...state,
        road: toggleValueInArray(action.payload, state.road),
      };
    }
    case EFilterActions.ChangeRoadDistance: {
      return {
        ...state,
        roadDistance: action.payload,
      };
    }
    case EFilterActions.AddStations: {
      return {
        ...state,
        station: action.payload,
      };
    }
    case EFilterActions.ChangeStations: {
      return {
        ...state,
        station: toggleValueInArray(action.payload, state.station),
      };
    }
    case EFilterActions.ChangeStationDistance: {
      return {
        ...state,
        stationDistance: action.payload,
      };
    }
    case EFilterActions.ResetExtraFilters: {
      return { ...state, ...DEFAULT_EXTRA_FILTERS };
    }
    case EFilterActions.RemoveFilters: {
      return action.payload;
    }
    case EFilterActions.RemoveLocationId: {
      return {
        ...state,
        locationId: undefined,
        locationType: undefined,
      };
    }
    case EFilterActions.ChangeFilters: {
      return {
        ...action.payload,
      };
    }
    case EFilterActions.ChangeBlackFridayFlag: {
      return {
        ...state,
        blackFriday: action.payload,
      };
    }
    default: {
      return state || getDefaultVillageListRequest();
    }
  }
};
